import { APP_PERMISSIONS } from '@exam-center/permission';
import { useAuth } from '@shared/auth';
import {
  DeleteOutlineIcon,
  MoreMenuIconButton,
  type MoreMenuIconButtonOption,
} from '@shared/components';
import { useDisclosure } from '@shared/hooks';
import { usePermission } from '@shared/permissions';
import { useCallback, useMemo } from 'react';

import { ExamCenterMemberDeletionModal } from '../exam-center-member-deletion-modal';

interface ExamCenterMemberMoreActionMenuProps {
  examCenterMemberId: string;
  userId: string;
}

interface ExamCenterMemberMoreActionMenuOption extends MoreMenuIconButtonOption {
  requiredPermissions: string[];
}

export default function ExamCenterMemberMoreActionMenu(
  props: Readonly<ExamCenterMemberMoreActionMenuProps>,
) {
  const { examCenterMemberId, userId } = props;

  const { permissions } = usePermission();
  const { userProfile } = useAuth();

  const [isDeletionModalOpen, { open: openDeletionModal, close: closeDeletionModal }] =
    useDisclosure(false);

  const handleDelete = useCallback(() => {
    openDeletionModal();
  }, [openDeletionModal]);

  const options: ExamCenterMemberMoreActionMenuOption[] = useMemo(() => {
    return [
      {
        label: 'ลบสมาชิก',
        icon: <DeleteOutlineIcon sx={(theme) => ({ color: theme.palette.error.main })} />,
        sx: (theme) => ({ color: theme.palette.error.main }),
        onClick: handleDelete,
        requiredPermissions: [APP_PERMISSIONS.ADMIN_EXAM_CENTER_MANAGEMENT.MEMBER.DELETE],
      },
    ];
  }, [handleDelete]);

  const filteredOptions = useMemo(() => {
    return options.filter((option) =>
      option.requiredPermissions.every((reqPermission) => permissions.includes(reqPermission)),
    );
  }, [options, permissions]);

  const shouldDisplayMoreActionMenu = useMemo(() => {
    return filteredOptions.length > 0 && userProfile?.id !== userId;
  }, [filteredOptions, userProfile?.id, userId]);

  if (!shouldDisplayMoreActionMenu) {
    return null;
  }

  return (
    <>
      <MoreMenuIconButton options={filteredOptions} />
      <ExamCenterMemberDeletionModal
        examCenterMemberId={examCenterMemberId}
        open={isDeletionModalOpen}
        onClose={closeDeletionModal}
      />
    </>
  );
}
